import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { memo } from "react";
import { pluralizeWithCount } from "@common/helpers/string";
import { Link } from "@design-system/Link/Link";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { useViewTableMeta } from "@hooks/view/useViewTableMeta";
import type { ApplyReportTemplateRow } from "../applyReportTemplateRows";
import styles from "../ApplyReportTemplate.module.scss";

export interface ApplyReportTemplateTableCallbacks {
    onOpenSettings: (row: ApplyReportTemplateRow) => void;
}

const getTooltip = ({ count, owner }: { count: number; owner: string }) => {
    if (count === 0) {
        return "Every setting comes from the template";
    }
    const verb = count === 1 ? "is" : "are";
    return `${pluralizeWithCount("setting", count)} ${verb} overridden on this ${owner}`;
};

export const ApplyReportTemplateOverridesCell = memo(
    ({
        row,
        table,
        getValue,
    }: CellContext<
        ViewTableFeatures,
        ApplyReportTemplateRow,
        number | null
    >) => {
        const meta = useViewTableMeta<
            ApplyReportTemplateTableCallbacks,
            undefined
        >(table);
        const count = getValue();
        if (count === null) {
            return <ViewTableCell />;
        }
        const owner = row.original.isClientRow ? "client" : "entity";
        const label =
            count === 0
                ? "No overrides"
                : pluralizeWithCount("override", count);
        return (
            <ViewTableCell>
                <Tooltip content={getTooltip({ count, owner })}>
                    {row.original.canOpenSettings ? (
                        <Link
                            size="sm"
                            className={styles.overridesLink}
                            onClick={(event) => {
                                event.stopPropagation();
                                meta.callbacks?.onOpenSettings(row.original);
                            }}
                        >
                            {label}
                        </Link>
                    ) : (
                        <span>{label}</span>
                    )}
                </Tooltip>
            </ViewTableCell>
        );
    },
);
